import dash_api from '../../../../main/support/dash_api';
import NotificationActions from '../../../../main/actions/NotificationActions';
import {
  trimGeneratedAdAccount,
  applyRandomAccountIdToAdNetwork,
  applyTimezoneToAdAccount,
  doesAdNetworkRequireAccountIdFromUser,
  getPartnerName,
  hasAdNetworkCostDataCredentialsFilled,
} from '../../../../main/components/PartnerManager/utils';
import { ReduxAsyncAction } from '../../../services/ReduxAsyncAction';
import type { SaveAdPartnerRequestActionPayload } from '../AdPartner.types';
import type { AdPartner, AdPartnerWithOnlyDataForSKAN } from 'client/js/main/types/adPartnerTypes';
import type { Action } from 'redux';
import { appTimezone } from 'client/js/main/support/dateUtils';
import { deepCopy } from '../../../../../../shared/utils';

export const trimPostbacks = (partner: AdPartner) => {
  const postbacks = partner.postbacks;
  const trimmedPostbacks = Array.isArray(postbacks)
    ? postbacks.map((p) => {
        p.template = p.template && p.template.trim();
        return p;
      })
    : postbacks;
  return { ...partner, postbacks: trimmedPostbacks };
};

const SaveAdPartner = new ReduxAsyncAction<
  'AD_PARTNER_SAVE_ENABLE',
  SaveAdPartnerRequestActionPayload,
  AdPartner | AdPartnerWithOnlyDataForSKAN
>('AD_PARTNER_SAVE_ENABLE');

SaveAdPartner.registerRequest(
  async ({
    partner,
    options = {
      withNotification: true,
      notificationMessage: '',
    },
  }) => {
    if ('postbacks' in partner) {
      trimPostbacks(partner);
    }

    let updatedPartner: AdPartner | AdPartnerWithOnlyDataForSKAN = deepCopy(partner);

    if ('credentials' in updatedPartner) {
      const insAccountSelectionEnabled = updatedPartner?.cost_ingestion_parameters?.account_info?.account_selection_enabled;
      if (insAccountSelectionEnabled) {
        trimGeneratedAdAccount(updatedPartner);
      } else if (
        hasAdNetworkCostDataCredentialsFilled(updatedPartner) &&
        !doesAdNetworkRequireAccountIdFromUser(updatedPartner)
      ) {
        updatedPartner = applyRandomAccountIdToAdNetwork(updatedPartner);
      }
      updatedPartner = applyTimezoneToAdAccount(updatedPartner, appTimezone);
    }

    const { data } = await dash_api.put<AdPartner>(`/v1/ad-networks/app`, {
      partner: updatedPartner,
      saveType: options?.saveType,
    });

    if ('credentials' in updatedPartner) {
      data.credentials = updatedPartner.credentials;
    }

    data.machine_name = updatedPartner.machine_name;
    data.neverConfigured = false;
    updatedPartner.enabled &&
      options &&
      options.withNotification &&
      NotificationActions.showNotification(
        options.notificationMessage || `${getPartnerName(partner)} has been saved and enabled`,
        'success'
      );
    return data;
  }
);

export const SAVE_ENABLE = SaveAdPartner.actionNamespaceConstants;

export const saveAdPartner = SaveAdPartner.request;

export type SaveAdPartnerResponseAction = Action<typeof SAVE_ENABLE.response> & { data: AdPartner };
